import { useState, useEffect, useCallback } from 'react';

interface Props {
  status: 'idle' | 'saving' | 'saved' | 'error' | 'pending';
  errorMessage?: string;
  onRetry?: () => void;
  pendingCount?: number;
}

export function SaveStatusIndicator({ status, errorMessage, onRetry, pendingCount }: Props) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);

    if (status === 'idle') {
      setVisible(false);
      return;
    }

    setVisible(true);

    if (status === 'saved') {
      const timer = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  if (!visible || status === 'idle' || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
      {status === 'saving' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-sm border border-blue-300/60 dark:border-blue-500/20">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Speichere...
        </div>
      )}

      {status === 'saved' && (
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm border border-green-300/60 dark:border-green-500/20">
          Gespeichert &#10003;
        </div>
      )}

      {status === 'error' && (
        <div className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm border border-red-300/60 dark:border-red-500/20 max-w-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">Speichern fehlgeschlagen</span>
            <button
              onClick={handleDismiss}
              className="text-red-400 hover:text-red-600 dark:hover:text-red-200 transition-colors p-0.5"
              aria-label="Schließen"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {errorMessage && (
            <p className="text-xs text-red-600 dark:text-red-400">{errorMessage}</p>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="self-start text-xs font-medium px-2.5 py-1 rounded bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      )}

      {status === 'pending' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-sm border border-amber-300/60 dark:border-amber-500/20 animate-pulse">
          Ungespeicherte Änderungen
          {pendingCount != null && pendingCount > 0 && (
            <span className="text-xs font-medium bg-yellow-200 dark:bg-yellow-800 px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
